import React, { useState, useEffect } from 'react';
import { Search, Plus, RefreshCw, Eye, MessageSquare, Clock, CheckCircle2, AlertCircle, Link as LinkIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/providers/AuthProvider';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mappings
const SUPPORT_CATEGORIES = [
  'Compte', 'Authentification', 'Produits', 'Images', 'IA',
  'Publication', 'Paiement', 'Performance', 'Sécurité', 'Bug', 'Autre'
];

const SUPPORT_PRIORITIES = {
  low: { label: '🔵 Low', color: 'blue' },
  medium: { label: '🟡 Medium', color: 'yellow' },
  high: { label: '🟠 High', color: 'orange' },
  critical: { label: '🔴 Critical', color: 'destructive' }
};

const SUPPORT_STATUSES = {
  new: { label: 'Nouveau', color: 'default' },
  assigned: { label: 'Assigné', color: 'blue' },
  waiting_user: { label: 'Attente utilisateur', color: 'purple' },
  waiting_team: { label: 'Attente équipe', color: 'indigo' },
  in_progress: { label: 'En cours', color: 'yellow' },
  resolved: { label: 'Résolu', color: 'green' },
  closed: { label: 'Fermé', color: 'secondary' }
};

const SupportAdmin = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    category: '',
    search: ''
  });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [updateData, setUpdateData] = useState({
    status: '',
    priority: '',
    assigned_to: '',
    status_comment: ''
  });
  const [newMessage, setNewMessage] = useState('');

  // Fetch tickets
  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.category) params.append('category', filters.category);
      if (filters.search) params.append('search', filters.search);

      const response = await axios.get(`${API}/admin/beta/support/tickets?${params.toString()}`, {
        withCredentials: true
      });

      setTickets(response.data.tickets);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
      toast.error('Erreur lors du chargement des tickets');
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/beta/support/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchTickets();
      fetchStats();
    }
  }, [user, filters]);

  // Open detail modal
  const handleViewDetails = async (ticket) => {
    try {
      const response = await axios.get(`${API}/admin/beta/support/tickets/${ticket.ticket_id}`, {
        withCredentials: true
      });
      setSelectedTicket(response.data);
      setUpdateData({
        status: response.data.status,
        priority: response.data.priority,
        assigned_to: response.data.assigned_to || '',
        status_comment: ''
      });
      setNewMessage('');
      setDetailModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch ticket details:', error);
      toast.error('Erreur lors du chargement des détails');
    }
  };

  // Update ticket
  const handleUpdate = async () => {
    if (!selectedTicket) return;

    try {
      await axios.patch(
        `${API}/admin/beta/support/tickets/${selectedTicket.ticket_id}`,
        updateData,
        { withCredentials: true }
      );

      toast.success('Ticket mis à jour avec succès');
      fetchTickets();
      fetchStats();
      
      // Refresh ticket details
      const response = await axios.get(`${API}/admin/beta/support/tickets/${selectedTicket.ticket_id}`, {
        withCredentials: true
      });
      setSelectedTicket(response.data);
    } catch (error) {
      console.error('Failed to update ticket:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Add message to ticket
  const handleAddMessage = async () => {
    if (!selectedTicket || !newMessage.trim()) {
      toast.error('Le message ne peut pas être vide');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/beta/support/tickets/${selectedTicket.ticket_id}/message`,
        { message: newMessage, attachments: [] },
        { withCredentials: true }
      );

      toast.success('Message ajouté avec succès');
      setNewMessage('');
      
      // Refresh ticket details
      const response = await axios.get(`${API}/admin/beta/support/tickets/${selectedTicket.ticket_id}`, {
        withCredentials: true
      });
      setSelectedTicket(response.data);
    } catch (error) {
      console.error('Failed to add message:', error);
      toast.error('Erreur lors de l\'ajout du message');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (user?.role !== 'admin') {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              Accès réservé aux administrateurs
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Support Center — Admin</h1>
        <p className="text-muted-foreground mt-2">
          Gestion des tickets de support Closed Beta
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Ouverts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.open_tickets}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                Fermés
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-green-600">{stats.closed_tickets}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Temps réponse moy.
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.avg_first_response_time_hours}h</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Temps résolution moy.
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{stats.avg_resolution_time_hours}h</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filtres</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher tickets..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Category filter */}
            <Select
              value={filters.category}
              onValueChange={(value) => setFilters({ ...filters, category: value === 'all' ? '' : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes catégories</SelectItem>
                {SUPPORT_CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Priority filter */}
            <Select
              value={filters.priority}
              onValueChange={(value) => setFilters({ ...filters, priority: value === 'all' ? '' : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Priorité" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes priorités</SelectItem>
                {Object.entries(SUPPORT_PRIORITIES).map(([key, { label }]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Status filter */}
            <Select
              value={filters.status}
              onValueChange={(value) => setFilters({ ...filters, status: value === 'all' ? '' : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                {Object.entries(SUPPORT_STATUSES).map(([key, { label }]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end mt-4">
            <Button variant="outline" size="sm" onClick={() => { fetchTickets(); fetchStats(); }}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      <Card>
        <CardHeader>
          <CardTitle>Tickets ({tickets.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-muted-foreground py-8">Chargement...</p>
          ) : tickets.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Aucun ticket trouvé
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Priorité</TableHead>
                    <TableHead>Sujet</TableHead>
                    <TableHead>Catégorie</TableHead>
                    <TableHead>Utilisateur</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tickets.map((ticket) => (
                    <TableRow key={ticket.ticket_id} className={ticket.priority === 'critical' ? 'bg-red-50 dark:bg-red-950/10' : ''}>
                      <TableCell>
                        <Badge variant={SUPPORT_PRIORITIES[ticket.priority]?.color || 'default'}>
                          {SUPPORT_PRIORITIES[ticket.priority]?.label || ticket.priority}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <div className="font-medium">{ticket.subject}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {ticket.description.substring(0, 60)}...
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{ticket.category}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{ticket.user_email}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={SUPPORT_STATUSES[ticket.status]?.color || 'default'}>
                          {SUPPORT_STATUSES[ticket.status]?.label || ticket.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(ticket.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(ticket)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      {selectedTicket && (
        <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
          <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedTicket.subject}</DialogTitle>
              <DialogDescription>
                Ticket #{selectedTicket.ticket_id.substring(0, 8)} • {selectedTicket.user_email}
              </DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="conversation" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="conversation">Conversation</TabsTrigger>
                <TabsTrigger value="details">Détails</TabsTrigger>
                <TabsTrigger value="admin">Gestion</TabsTrigger>
              </TabsList>

              <TabsContent value="conversation" className="space-y-4">
                {/* Messages */}
                <div className="space-y-3 max-h-96 overflow-y-auto p-4 bg-muted/30 rounded-lg">
                  {selectedTicket.messages.length === 0 ? (
                    <p className="text-center text-sm text-muted-foreground py-8">
                      Aucun message encore
                    </p>
                  ) : (
                    selectedTicket.messages.map((msg, idx) => (
                      <div
                        key={msg.message_id}
                        className={`p-3 rounded-lg ${
                          msg.from_type === 'admin'
                            ? 'bg-blue-50 dark:bg-blue-950/20 ml-8'
                            : 'bg-white dark:bg-slate-800 mr-8'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <Badge variant={msg.from_type === 'admin' ? 'default' : 'secondary'}>
                            {msg.from_type === 'admin' ? '👤 Admin' : '👥 Utilisateur'} • {msg.from_user}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(msg.timestamp)}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      </div>
                    ))
                  )}
                </div>

                {/* Add message */}
                <div className="space-y-2">
                  <Label htmlFor="new-message">Répondre au ticket</Label>
                  <Textarea
                    id="new-message"
                    placeholder="Votre réponse..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    rows={4}
                  />
                  <div className="flex justify-end">
                    <Button onClick={handleAddMessage}>
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Envoyer
                    </Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="details" className="space-y-4">
                {/* Description */}
                <div className="space-y-2">
                  <Label className="font-medium">Description</Label>
                  <p className="text-sm p-3 bg-muted rounded-md whitespace-pre-wrap">
                    {selectedTicket.description}
                  </p>
                </div>

                {/* Context */}
                <div className="space-y-2">
                  <Label className="font-medium">Informations</Label>
                  <div className="text-xs space-y-1 p-3 bg-muted rounded-md">
                    <p><strong>Workspace:</strong> {selectedTicket.workspace_id || 'N/A'}</p>
                    <p><strong>Créé le:</strong> {formatDate(selectedTicket.created_at)}</p>
                    <p><strong>Dernière MAJ:</strong> {formatDate(selectedTicket.updated_at)}</p>
                    <p><strong>Assigné à:</strong> {selectedTicket.assigned_to || 'Non assigné'}</p>
                  </div>
                </div>

                {/* Linked items */}
                {(selectedTicket.linked_feedback_ids.length > 0 || selectedTicket.linked_incident_ids.length > 0) && (
                  <div className="space-y-2">
                    <Label className="font-medium">Éléments liés</Label>
                    <div className="text-xs space-y-1 p-3 bg-muted rounded-md">
                      {selectedTicket.linked_feedback_ids.length > 0 && (
                        <p><strong>Feedbacks liés:</strong> {selectedTicket.linked_feedback_ids.length}</p>
                      )}
                      {selectedTicket.linked_incident_ids.length > 0 && (
                        <p><strong>Incidents liés:</strong> {selectedTicket.linked_incident_ids.length}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Status history */}
                <div className="space-y-2">
                  <Label className="font-medium">Historique des statuts</Label>
                  <div className="space-y-2">
                    {selectedTicket.status_history.map((entry, idx) => (
                      <div key={idx} className="p-2 border rounded-lg text-xs">
                        <div className="flex justify-between items-center">
                          <Badge variant={SUPPORT_STATUSES[entry.status]?.color || 'default'} className="text-xs">
                            {SUPPORT_STATUSES[entry.status]?.label || entry.status}
                          </Badge>
                          <span className="text-muted-foreground">{formatDate(entry.changed_at)}</span>
                        </div>
                        <p className="mt-1">{entry.changed_by}: {entry.comment}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="admin" className="space-y-4">
                {/* Status */}
                <div className="space-y-2">
                  <Label htmlFor="update-status">Statut</Label>
                  <Select
                    value={updateData.status}
                    onValueChange={(value) => setUpdateData({ ...updateData, status: value })}
                  >
                    <SelectTrigger id="update-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(SUPPORT_STATUSES).map(([key, { label }]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Priority */}
                <div className="space-y-2">
                  <Label htmlFor="update-priority">Priorité</Label>
                  <Select
                    value={updateData.priority}
                    onValueChange={(value) => setUpdateData({ ...updateData, priority: value })}
                  >
                    <SelectTrigger id="update-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(SUPPORT_PRIORITIES).map(([key, { label }]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Status comment */}
                <div className="space-y-2">
                  <Label htmlFor="status-comment">Commentaire de changement</Label>
                  <Input
                    id="status-comment"
                    placeholder="Raison du changement..."
                    value={updateData.status_comment}
                    onChange={(e) => setUpdateData({ ...updateData, status_comment: e.target.value })}
                  />
                </div>

                <div className="flex justify-end">
                  <Button onClick={handleUpdate}>
                    Enregistrer les modifications
                  </Button>
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailModalOpen(false)}>
                Fermer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default SupportAdmin;
